"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.agentMentionQuerySchema = exports.agentConversationListQuerySchema = exports.agentMentionListSchema = exports.agentMentionOptionSchema = exports.AGENT_MENTION_KINDS = exports.agentDashboardViewSchema = exports.AGENT_DASHBOARD_TABS = void 0;
exports.periodMentionId = periodMentionId;
exports.mentionToken = mentionToken;
exports.messageParts = messageParts;
exports.parseMentions = parseMentions;
const zod_1 = require("zod");
const eac_1 = require("../contracts/eac");
const evm_1 = require("../contracts/evm");
/* A calendar date on the wire — `YYYY-MM-DD`, never an instant. */
const wireDate = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 19 Oct 2026 · THE DASHBOARD CHAT — what it sends beside the question.     │
 * │ decisions.md AG-1.                                                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The chat bar on a project's Áttekintés and Pénzügy tabs answers only about what is on that
 * dashboard. Its threads are ordinary Agent conversations with `origin = 'dashboard'` and a project
 * (`0222`); what this file adds is the two things a question from a dashboard carries that one from
 * the Agent page does not:
 *
 *  * **the view** — which tab, which contract, line item or period is picked, the past date the
 *    lookback bar is set to, the EAC type, the columns. *"Miért ilyen magas?"* means nothing without
 *    it. It travels with EVERY message, because the person changes the view between questions;
 *  * **mentions** — `@` a contract, line item, period, person, task, invoice or external cost.
 */
/** Which of the project's two dashboards the bar sits on. */
exports.AGENT_DASHBOARD_TABS = ['overview', 'finance'];
/**
 * What the person is looking at when they ask.
 *
 * ⚠️ **Every field but the tab is optional**, because each tab draws a different subset: the
 * Áttekintés tab has no EAC switch and no external-cost toggle. An absent field is *"the dashboard's
 * default"*, never *"unknown"* — `contractId` absent is the whole project.
 *
 * ⚠️ **Display state, not authority.** The server resolves every id in it through the same pinned
 * reads the tools use, so a contract id from another project is refused rather than described.
 */
exports.agentDashboardViewSchema = zod_1.z.strictObject({
    tab: zod_1.z.enum(exports.AGENT_DASHBOARD_TABS),
    contractId: zod_1.z.uuid().optional(),
    lineItemId: zod_1.z.uuid().optional(),
    occurrenceStartsOn: wireDate.optional(),
    /** The lookback bar's cut. Absent is live. */
    asOf: wireDate.optional(),
    eacMode: zod_1.z.enum(eac_1.EAC_MODES).optional(),
    granularity: zod_1.z.enum(evm_1.FINANCE_GRANULARITIES).optional(),
    includeExternal: zod_1.z.boolean().optional(),
});
/* ── mentions ─────────────────────────────────────────────────────────────────────────────────── */
/**
 * What can be `@`-mentioned from a dashboard. The product owner's list, 19 Oct 2026.
 *
 * ⚠️ `period` is a recurring contract's occurrence, which has no row of its own — its id is
 * `<contractId>~<startsOn>` (`periodMentionId`).
 */
exports.AGENT_MENTION_KINDS = [
    'contract',
    'lineItem',
    'period',
    'person',
    'task',
    'invoice',
    'externalCost',
];
function periodMentionId(contractId, startsOn) {
    return `${contractId}~${startsOn}`;
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A MENTION IS A TOKEN IN THE TEXT — `@[HSO-C01 · Build](contract:id)`. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Not a separate list beside the text, for three reasons:
 *
 *  * **the stored message is the text**, and a reopened thread has to draw the chips where they
 *    were typed — a list beside it would say *which* but not *where*;
 *  * **the model reads it as written**: the label is right there, and the turn note resolves each
 *    token to the entity's figures;
 *  * **one string cannot disagree with itself.** A list and a text sent side by side can — a chip
 *    deleted from the text and left in the list would be a mention of something no longer asked
 *    about.
 *
 * ⚠️ The label may not contain `]` and the id may not contain `)`; `mentionToken` strips both so a
 * contract named *"Build [phase 2]"* still makes one token rather than a broken one.
 */
function mentionToken(mention) {
    const label = mention.label.replace(/[[\]]/gu, '').trim();
    return `@[${label}](${mention.kind}:${mention.id.replace(/[()]/gu, '')})`;
}
const MENTION_PATTERN = new RegExp(String.raw `@\[([^\]\n]{1,200})\]\((${exports.AGENT_MENTION_KINDS.join('|')}):([^)\s]{1,120})\)`, 'gu');
/** The text cut into plain runs and mentions, in order — for drawing chips where they were typed. */
function messageParts(text) {
    const parts = [];
    let last = 0;
    for (const match of text.matchAll(MENTION_PATTERN)) {
        const at = match.index;
        if (at > last)
            parts.push({ type: 'text', text: text.slice(last, at) });
        parts.push({
            type: 'mention',
            label: match[1] ?? '',
            kind: (match[2] ?? 'contract'),
            id: match[3] ?? '',
        });
        last = at + match[0].length;
    }
    if (last < text.length)
        parts.push({ type: 'text', text: text.slice(last) });
    return parts;
}
/** Every mention in a message, in order, each once. */
function parseMentions(text) {
    const seen = new Set();
    const out = [];
    for (const part of messageParts(text)) {
        if (part.type !== 'mention')
            continue;
        const key = `${part.kind}:${part.id}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push({ kind: part.kind, id: part.id, label: part.label });
    }
    return out;
}
/**
 * One thing the `@` picker offers.
 *
 * ⚠️ `detail` is the grey text beside the label — an email, a contract code, a date range — and
 * `avatarUrl` / `color` are for the leading mark. All three are data, never our words (rule 7).
 */
exports.agentMentionOptionSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.AGENT_MENTION_KINDS),
    id: zod_1.z.string(),
    label: zod_1.z.string(),
    detail: zod_1.z.string().nullable(),
    avatarUrl: zod_1.z.string().nullable(),
    color: zod_1.z.string().nullable(),
});
/**
 * The picker's list for one project: every kind the reader may see, filtered by what was typed.
 *
 * ⚠️ **`total` per kind, so the group can say *"Show 34 more…"*** without the server sending all
 * thirty-four. A kind the reader may not read is simply absent — its absence is not a count of 0.
 */
exports.agentMentionListSchema = zod_1.z.object({
    groups: zod_1.z.array(zod_1.z.object({
        kind: zod_1.z.enum(exports.AGENT_MENTION_KINDS),
        total: zod_1.z.number().int().nonnegative(),
        options: zod_1.z.array(exports.agentMentionOptionSchema),
    })),
});
/** `GET /agent/conversations?projectId=` — one project's dashboard threads; absent is the Agent page's. */
exports.agentConversationListQuerySchema = zod_1.z.object({
    projectId: zod_1.z.uuid().optional(),
});
/** `GET /agent/dashboard/:projectId/mentions?q=&kind=` — `kind` narrows to one group, raising its page. */
exports.agentMentionQuerySchema = zod_1.z.object({
    q: zod_1.z.string().max(100).optional(),
    kind: zod_1.z.enum(exports.AGENT_MENTION_KINDS).optional(),
});
//# sourceMappingURL=dashboard.js.map